import React, { useCallback, useState, useEffect, memo } from "react";
import moment, { Moment } from "moment";
import memoizeOne from "memoize-one";
import debug from "../debug";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { TimelineEvent, BoardState } from "../tools/events";
import { Labels } from "../types";
import { cfd } from "../tools/cfd";
import ProgressBar from "./ProgressBar";

import "./Timeline.css";

const log = debug("Timeline");

require("highcharts/modules/timeline")(Highcharts);

interface TimelineCfdChartProps {
    chartOptions: Highcharts.Options;
}

const TimelineCfdChart: React.FC<TimelineCfdChartProps> = function ({ chartOptions }: TimelineCfdChartProps) {
    return (
        <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            allowChartUpdate={true}
            updateArgs={[true, true, true]}
        />
    );
}

const PureTimelineCfdChart = memo(TimelineCfdChart);

interface Props {
    startBoard: BoardState;
    events: TimelineEvent[];
    labels: Labels;
    columnId: string;
    from: Moment;
    to: Moment;
    currentTime: Moment;
    isPlaying: boolean;
    timeUpdated: (newTime: Moment) => void;
}

const cfdSeries = memoizeOne(
    (startBoard: BoardState, events: TimelineEvent[], columnId: string, labels: Labels, from: Moment, to: Moment) => {
        return cfd(startBoard, events, columnId, labels, from, to, true);
    }
);

const Timeline: React.FC<Props> = function ({
    startBoard,
    events,
    labels,
    columnId,
    from,
    to,
    currentTime,
    isPlaying,
    timeUpdated
}: Props) {
    const onClick: Highcharts.ChartClickCallbackFunction = useCallback(
        (e) => {
            const x = (e as any).xAxis[0].value;
            timeUpdated(moment(x));
        },
        [timeUpdated]
    );

    const onRender: Highcharts.ChartRenderCallbackFunction = useCallback((e) => {
        log(`timeline chart onRender: ${e.type}`);
    }, []);

    const axisColor = "var(--primary-font-color)";

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>({
        credits: {
            enabled: false
        },
        chart: {
            events: {
                click: onClick,
                render: onRender
            },
            backgroundColor: "var(--primary-background-color)",
            height: 150,
            spacing: [0, 0, 0, 0],
            marginRight: 0
            // margin: [0, 10, 20, 10]
        },
        title: {
            floating: true,
            text: undefined
        },
        xAxis: {
            type: "datetime",
            lineColor: "#888",
            lineWidth: 8,
            tickColor: axisColor,
            labels: {
                style: {
                    color: axisColor,
                    fontFamily: "Roboto"
                }
            },
            min: from.valueOf(),
            max: to.valueOf()
        },
        yAxis: [
            {
                gridLineWidth: 1,
                minTickInterval: 1,
                title: {
                    text: null
                },
                labels: { enabled: false }
            }
        ],
        legend: {
            enabled: false
        },
        tooltip: {
            enabled: false
        },
        plotOptions: {
            area: {
                dragDrop: {
                    draggableX: false
                },
                enableMouseTracking: false,
                stacking: "normal",
                marker: {
                    enabled: false
                },
                states: {
                    hover: {
                        animation: false,
                        enabled: false
                    }
                }
            }
        },
        time: {
            useUTC: false
        },
        series: []
    });

    useEffect(() => {
        log("useEffect - set chart options");
        setChartOptions({
            xAxis: {
                type: "datetime",
                min: from.valueOf(),
                max: to.valueOf()
            },
            series: [...cfdSeries(startBoard, events, columnId, labels, from, to)]
        });
    }, [startBoard, events, columnId, labels, from, to]);

    useEffect(() => {
        return (): void => {
            // todo: unregister event listeners? highcharts seems to have problems when changing views
        };
    }, []);

    return (
        <div className="timeline">
            <PureTimelineCfdChart chartOptions={chartOptions} />
            <ProgressBar
                className="timeline__progress"
                from={from}
                to={to}
                currentTime={currentTime}
                isPlaying={isPlaying}
                timeUpdated={timeUpdated}
            />
        </div>
    );
}

export default Timeline;